import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';

import type { EsnIconName } from '../../../shared/icons';
import type { EsnContentType } from '../../../shared/types/atoms';
import type { EsnTreeRowKind } from '../../../shared/types/molecules';
import { ContentTypeIconComponent } from '../../atoms/content-type-icon/content-type-icon.component';
import { IconComponent } from '../../atoms/icon/icon.component';

const CONTAINERS: readonly EsnTreeRowKind[] = ['world', 'building', 'accent'];
const SLOTS: readonly EsnTreeRowKind[] = ['slot-linked', 'slot-empty', 'slot-active', 'slot-active-empty'];
const CURRENT: readonly EsnTreeRowKind[] = ['slot-active', 'slot-active-empty', 'content-active'];

/**
 * One visible row of a space tree: world, building, slot or content, indented by depth, with
 * an expand chevron on containers and the current row highlighted. The label is projected.
 */
@Component({
  selector: 'esn-tree-row',
  imports: [IconComponent, ContentTypeIconComponent],
  templateUrl: './tree-row.component.html',
  styleUrl: './tree-row.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'treeitem',
    '[attr.aria-level]': 'level() + 1',
    '[attr.aria-selected]': 'isCurrent()',
    '[attr.aria-expanded]': 'isContainer() ? expanded() : null',
  },
})
export class TreeRowComponent {
  /**
   * Horizontal step per tree level, in px; `0` keeps a deep tree flush, as the Space Tree draws it.
   */
  readonly indent = input(16);

  /**
   * Stretch the row across its container (the Space Tree), instead of hugging its content.
   */
  readonly fullWidth = input(false);

  /**
   * Leave the chevron out of the Tab order when the surrounding tree handles the keyboard itself.
   */
  readonly chevronTabbable = input(true);

  readonly kind = input<EsnTreeRowKind>('building');

  /**
   * Depth in the tree; 0 is the top level.
   */
  readonly level = input(0);

  /**
   * Open state of a container row (world / building / accent); toggled by its chevron.
   */
  readonly expanded = model(true);

  /**
   * Trailing caption, e.g. "(Building)".
   */
  readonly meta = input<string | null>(null);

  /**
   * Trailing tag, e.g. "Your Assigned Space" on the accent row.
   */
  readonly chip = input<string | null>(null);

  /**
   * Type glyph in front of a content row's name.
   */
  readonly contentIcon = input<EsnIconName>('video');

  /**
   * Medium of a content row; when set it draws the Content Type Icon (with its external badge) instead.
   */
  readonly contentType = input<EsnContentType | null>(null);

  /**
   * The content row's asset is a URL rather than an upload (needs `contentType`).
   */
  readonly external = input(false);

  protected readonly isContainer = computed(() => CONTAINERS.includes(this.kind()));
  protected readonly isSlot = computed(() => SLOTS.includes(this.kind()));
  protected readonly isCurrent = computed(() => CURRENT.includes(this.kind()));

  protected readonly classes = computed(() =>
    `esn-tree-row esn-tree-row--${this.kind()}` +
      (this.isCurrent() ? ' esn-tree-row--current' : '') +
      (this.fullWidth() ? ' esn-tree-row--full' : ''));

  protected toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.expanded.update((open) => !open);
  }
}
